import type { GameState, Player, PointState, LegalTarget, ReinePickup, MotherOutcome } from './types';
import { cloneState, liftPiece, HOME_RANGE, START_POINT, opponent } from './state';

// Can `player` land their checker on `dest`?
export function canLand(points: PointState[], dest: number, player: Player): boolean {
  if (dest < 1 || dest > 24) return false;
  const pt = points[dest]!;
  if (pt.topOwner === null) return true;
  if (pt.topOwner === player) return true;
  // Enemy on top
  if (pt.topCount >= 2) return false;
  // Lone enemy: pin allowed unless it is already pinning one of our pieces
  return pt.pinnedOwner !== player;
}

// Count all checkers (top + pinned) belonging to `player` at each point.
export function checkerCount(points: PointState[], player: Player): Map<number, number> {
  const m = new Map<number, number>();
  for (let i = 1; i <= 24; i++) {
    const pt = points[i]!;
    let n = 0;
    if (pt.topOwner === player) n += pt.topCount;
    if (pt.pinnedOwner === player) n += 1;
    if (n > 0) m.set(i, n);
  }
  return m;
}

// Are all of `player`'s checkers (including pinned) inside their home board?
export function allCheckersHome(points: PointState[], player: Player): boolean {
  const [lo, hi] = HOME_RANGE[player];
  for (let i = 1; i <= 24; i++) {
    if (i >= lo && i <= hi) continue;
    const pt = points[i]!;
    if (pt.topOwner === player && pt.topCount > 0) return false;
    if (pt.pinnedOwner === player) return false;
  }
  return true;
}

// Can `player` bear off a top-stack checker from `from` using `dieValue`?
// Requires all of the player's checkers (including pinned) to be in the home range first.
function canBearOff(points: PointState[], player: Player, from: number, dieValue: number): boolean {
  if (!allCheckersHome(points, player)) return false;
  const [lo, hi] = HOME_RANGE[player];
  if (from < lo || from > hi) return false;
  const dest = player === 'white' ? from - dieValue : from + dieValue;
  return player === 'white' ? dest <= 0 : dest >= 25;
}

// Compute the destination point for a forward move.
function moveDest(player: Player, from: number, die: number): number {
  return player === 'white' ? from - die : from + die;
}

// All legal (from, to, dieIndex) combinations for the current player.
export function getLegalMoves(state: GameState): Array<{ from: number; to: number; dieIndex: number }> {
  const { points, currentPlayer, dice, diceUsed } = state;
  const results: Array<{ from: number; to: number; dieIndex: number }> = [];

  const unusedDice = dice
    .map((d, i) => ({ d, i }))
    .filter(({ i }) => !diceUsed[i]);

  for (let from = 1; from <= 24; from++) {
    const pt = points[from]!;
    if (pt.topOwner !== currentPlayer || pt.topCount === 0) continue;

    for (const { d, i } of unusedDice) {
      const dest = moveDest(currentPlayer, from, d);
      if (dest >= 1 && dest <= 24) {
        if (canLand(points, dest, currentPlayer)) {
          results.push({ from, to: dest, dieIndex: i });
        }
      } else {
        if (canBearOff(points, currentPlayer, from, d)) {
          results.push({ from, to: 0, dieIndex: i });
        }
      }
    }
  }

  // For bear-off moves: when multiple dice can score the same piece, keep only the
  // smallest die (preserves larger dice for pieces farther from the exit).
  const nonBearOff = results.filter(m => m.to !== 0);
  const bearOffBest = new Map<number, typeof results[number]>();
  for (const m of results.filter(m => m.to === 0)) {
    const cur = bearOffBest.get(m.from);
    if (!cur || dice[m.dieIndex]! < dice[cur.dieIndex]!) bearOffBest.set(m.from, m);
  }
  return [...nonBearOff, ...bearOffBest.values()];
}

// Deduplicate getLegalMoves into LegalTarget[] per source point.
export function getLegalTargetsForPoint(
  state: GameState,
  from: number,
): LegalTarget[] {
  const moves = getLegalMoves(state).filter(m => m.from === from);
  const map = new Map<number, number[]>();
  for (const { to, dieIndex } of moves) {
    const arr = map.get(to) ?? [];
    arr.push(dieIndex);
    map.set(to, arr);
  }
  return Array.from(map.entries()).map(([to, dieIndices]) => ({ to, dieIndices }));
}

// For reine mode: compute valid placements for the picked-up piece.
// The piece is temporarily absent from the board (state should already reflect the lift).
export function getReinePlacements(
  stateAfterLift: GameState,
  pickup: ReinePickup,
): LegalTarget[] {
  const { points } = stateAfterLift;
  const targets: LegalTarget[] = [];
  for (let dest = 1; dest <= 24; dest++) {
    if (canLand(points, dest, pickup.owner)) {
      targets.push({ to: dest, dieIndices: [] });
    }
  }
  // Bear-off: allowed in reine mode if the lifted piece came from the home range
  const [rlo, rhi] = HOME_RANGE[pickup.owner];
  if (pickup.point >= rlo && pickup.point <= rhi) {
    targets.push({ to: 0, dieIndices: [] });
  }
  return targets;
}

// Check for mother-loss immediately after a move that may have pinned the opponent.
// `movingPlayer` is the player who just moved.
export function checkMotherLoss(state: GameState, movingPlayer: Player): MotherOutcome | null {
  const opp = opponent(movingPlayer);
  const oppStart = START_POINT[opp];
  const moverStart = START_POINT[movingPlayer];

  const pt = state.points[oppStart]!;
  // Opponent's starting point must have a pinned checker to trigger
  if (pt.topOwner !== movingPlayer || pt.pinnedOwner !== opp) return null;
  // Opponent must not have moved yet (mother still on start)
  if (state.hasMoved[opp]) return null;

  // Both mothers pinned simultaneously → tie
  const moverPt = state.points[moverStart]!;
  if (!state.hasMoved[movingPlayer] && moverPt.topOwner === opp && moverPt.pinnedOwner === movingPlayer) {
    return 'tie';
  }

  // Check waiver: if moving player still has checkers at their own starting point
  const moverStartPt = state.points[moverStart]!;
  if (
    moverStartPt.topOwner === movingPlayer ||
    moverStartPt.pinnedOwner === movingPlayer
  ) {
    return null; // waived
  }

  return opp === 'white' ? 'white-loses' : 'black-loses';
}

// Check if someone has won. Call after any bear-off.
export function checkGameOver(
  state: GameState,
): { winner: Player; mars: boolean } | null {
  for (const p of ['white', 'black'] as Player[]) {
    if (state.borneOff[p] === 15) {
      const opp = p === 'white' ? 'black' : 'white';
      const mars = state.borneOff[opp] === 0;
      return { winner: p, mars };
    }
  }
  return null;
}

// Does the current player have any legal moves with the remaining dice?
export function hasAnyLegalMove(state: GameState): boolean {
  return getLegalMoves(state).length > 0;
}
